import { useCallback, useEffect, useMemo } from 'react';
import { Button } from '@/components/presentation/Button';
import { GrayOutlineButton } from '@/components/presentation/ButtonOutlined';
import { useCanSaveObjectAccess, useFetchObjectAccess, useGroupUsersContext, useObjectAccessLoading, useObjectAccessState, useSaveObjectAccess } from '@/containers/ObjectAccess/hooks';
import { WorkspaceObjectRole, WorkspaceRoleMap } from '@/enums';
import type { GroupUser } from '@/types/group';
import type { ObjectAccessUser } from '@/types/workspace.access';
import { useLock } from '@/utils/hooks';
import GroupUsersAutocomplete from './GroupUsersAutocomplete';
import styles from './style/ObjectAccess.module.css';
import UserObjectAccessPopper from './UserAccessContextMenu';
import { sortObjectAccess } from './utils';

type Props = {
  onCancel: () => unknown;
};

export const ObjectAccessForm = ({ onCancel }: Props) => {
  const loading = useObjectAccessLoading();
  const [state, dispatch] = useObjectAccessState();
  const canSave = useCanSaveObjectAccess();
  const saveAccess = useSaveObjectAccess();
  const [_, lock] = useLock();
  const fetchAccess = useFetchObjectAccess();
  const { fetch: fetchGroupUsers } = useGroupUsersContext();

  useEffect(() => { fetchGroupUsers(); }, [fetchGroupUsers]);
  useEffect(() => { fetchAccess(); }, [fetchAccess]);

  const sorted = useMemo(() => state.items.sort(sortObjectAccess), [state.items]);

  const renderRow = useCallback((item: ObjectAccessUser) => {
    const name = !item.user.firstName || !item.user.lastName
      ? item.user.email
      : [item.user.firstName, item.user.lastName].join(' ');

    return (
      <div key={item.user.id} className={styles.row}>
        <div className={styles.col}>{name}</div>
        <div className={styles.col}>
          {item.roleId === WorkspaceObjectRole.Owner ?
            <div className={styles.owner}>
              {WorkspaceRoleMap[item.roleId]}
            </div>
            : <UserObjectAccessPopper item={item} />
          }
        </div>
      </div>
    );
  }, []);

  const addUser = useCallback((data: GroupUser) => {
    dispatch({
      value: {
        roleId: WorkspaceObjectRole.Viewer,
        user: {
          email: data.email,
          firstName: data.firstName,
          id: data.id,
          lastName: data.lastName,
        },
      },
      type: 'add',
    });
  }, [dispatch]);

  const handleSave = useCallback(() => {
    return saveAccess()
      .then(onCancel);
  }, [
    onCancel,
    saveAccess,
  ]);

  return (
    <div className={styles.root}>
      <GroupUsersAutocomplete
        className={styles.autocomplete}
        onSelect={addUser} />
      <div className={styles.header}>
        <div className={styles.col}>Who has access</div>
        <div className={styles.col} />
      </div>
      <div className={styles.body}>
        {!loading && sorted.map(renderRow)}
      </div>
      <div className={styles.footer}>
        <GrayOutlineButton
          className={styles.btn}
          onClick={onCancel}
          title="Cancel" />
        <Button.Affirmative
          className={styles.btn}
          disabled={!canSave}
          onClick={lock(handleSave)}
          variant="brick"
          title="Save" />
      </div>
    </div>
  );
};

export default ObjectAccessForm;